import React, { useEffect, useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import SubscriptionService from "../../services/SubscriptionService";
import UserService from "../../services/UserService";
import JigsawService from "../puzzles/jigsaw/JigsawService";
import { usePagesTranslation } from "../locales";
import { jigsawPackCheckoutPlan } from "./jigsawPackOffers";
import { browseViewButtonSx } from "./JigsawBrowseStoryCard";

function moneyLabel(priceUsd) {
    const amount = Number(priceUsd);
    if (!Number.isFinite(amount)) return "";
    return `$${amount.toFixed(2)}`;
}

function packsFromApi(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((row) => row?.packId && row.published === true)
        .map((row) => ({
            id: String(row.packId),
            title: row.title || String(row.packId),
            price: row.tier === "free"
                ? "Free"
                : (moneyLabel(row.priceUsd) || (row.tier === "t1" ? "$0.99" : "$1.99")),
            tier: row.tier === "free" || row.tier === "t1" || row.tier === "t2" ? row.tier : "t2",
            pictures: Array.isArray(row.pictures) ? row.pictures : [],
            owned: Boolean(row.owned),
        }));
}

export default function JigsawPremiumPacks() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [signedIn, setSignedIn] = useState(false);
    const [accessReady, setAccessReady] = useState(false);
    const [packs, setPacks] = useState([]);
    const [loadingPacks, setLoadingPacks] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [busyId, setBusyId] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        let cancel = false;
        UserService.getUserInfo()
            .then((data) => {
                if (cancel) return;
                setSignedIn(Boolean(data?.cloudUser));
            })
            .catch(() => {
                if (cancel) return;
                setSignedIn(false);
            })
            .finally(() => {
                if (!cancel) setAccessReady(true);
            });
        JigsawService.listPacks()
            .then((rows) => {
                if (!cancel) setPacks(packsFromApi(rows));
            })
            .catch(() => {
                if (!cancel) {
                    setPacks([]);
                    setLoadError("failed");
                }
            })
            .finally(() => {
                if (!cancel) setLoadingPacks(false);
            });
        return () => {
            cancel = true;
        };
    }, []);

    useEffect(() => {
        if (!signedIn) return undefined;
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        if (!sessionId || sessionId.includes("{")) return undefined;
        let cancel = false;
        SubscriptionService.confirmCheckoutSession(sessionId)
            .catch(() => {})
            .finally(() => {
                if (cancel) return;
                params.delete("session_id");
                const search = params.toString();
                navigate(
                    { pathname: location.pathname, search: search ? `?${search}` : "" },
                    { replace: true },
                );
                JigsawService.listPacks()
                    .then((rows) => {
                        if (!cancel) setPacks(packsFromApi(rows));
                    })
                    .catch(() => {});
            });
        return () => {
            cancel = true;
        };
    }, [signedIn, location.pathname, location.search, navigate]);

    const buy = async (offer) => {
        if (offer?.tier === "free") return;
        if (!signedIn) {
            navigate("/login", {
                state: { from: { pathname: `${location.pathname}${location.search || ""}` } },
            });
            return;
        }
        setBusyId(offer.id);
        setError("");
        try {
            const userData = await UserService.getUserInfo();
            const email = userData?.user?.email || userData?.cloudUser?.email;
            if (!email) {
                setError(t("puzzles-browse.jigsaw-pack-checkout-error", "Could not start checkout."));
                return;
            }
            const result = await SubscriptionService.createCheckoutSession(jigsawPackCheckoutPlan(offer), email);
            const url = result?.data?.url || result?.data?.checkoutUrl;
            if (!result.success || !url) {
                setError(result.error || t("puzzles-browse.jigsaw-pack-checkout-error", "Could not start checkout."));
                return;
            }
            window.location.assign(url);
        } catch (checkoutError) {
            setError(checkoutError?.message || t("puzzles-browse.jigsaw-pack-checkout-error", "Could not start checkout."));
        } finally {
            setBusyId("");
        }
    };

    if (!accessReady) {
        return null;
    }

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Button
                component={RouterLink}
                to="/puzzles/jigsaw"
                sx={{ mb: 1, minHeight: 44, textTransform: "none" }}
            >
                {t("puzzles-browse.back-list", "All puzzles")}
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t("puzzles-browse.jigsaw-premium-packs", "Premium packs")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t(
                    "puzzles-browse.jigsaw-premium-packs-blurb",
                    "Picture packs for the web. Buy once and the puzzles in that pack stay unlocked on your account.",
                )}
            </Typography>
            {error ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            ) : null}
            {loadError ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {t("puzzles-browse.jigsaw-premium-packs-error", "Could not load published packs.")}
                </Alert>
            ) : null}
            {loadingPacks ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : packs.length === 0 && !loadError ? (
                <Typography variant="body1" color="text.secondary">
                    {t("puzzles-browse.jigsaw-premium-packs-empty", "No published packs yet.")}
                </Typography>
            ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {packs.map((offer) => {
                    const owned = Boolean(offer.owned);
                    const isFree = offer.tier === "free";
                    return (
                        <Box
                            key={offer.id}
                            sx={{
                                pb: 2.5,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    mb: 1.5,
                                }}
                            >
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
                                    {offer.title}
                                </Typography>
                                <Button
                                    variant="contained"
                                    disabled={isFree || owned || busyId === offer.id}
                                    onClick={() => buy(offer)}
                                    startIcon={isFree || owned ? null : <LockOutlinedIcon />}
                                    sx={{
                                        ...browseViewButtonSx,
                                        minHeight: 44,
                                        "&.Mui-disabled": {
                                            backgroundColor: "rgba(56, 132, 255, 0.12)",
                                            color: "rgba(226, 232, 240, 0.74)",
                                            border: "1px solid rgba(96, 165, 250, 0.35)",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    {isFree
                                        ? t("puzzles-browse.jigsaw-pack-free", "Free")
                                        : owned
                                        ? t("puzzles-browse.jigsaw-pack-owned", "Owned")
                                        : t("puzzles-browse.jigsaw-pack-buy", { price: offer.price })}
                                </Button>
                            </Box>
                            {offer.pictures?.length ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                                    {offer.pictures.map((picture) => (
                                        <Box
                                            key={picture.storyId || picture.title}
                                            {...(picture.storyId
                                                ? {
                                                      component: RouterLink,
                                                      to: `/puzzle/play-jigsaw/${encodeURIComponent(picture.storyId)}`,
                                                  }
                                                : {})}
                                            sx={{
                                                width: 140,
                                                flex: "0 0 auto",
                                                display: "block",
                                                textDecoration: "none",
                                                color: "inherit",
                                                borderRadius: 1,
                                                minHeight: 44,
                                                ...(picture.storyId
                                                    ? {
                                                          cursor: "pointer",
                                                          "&:hover": {
                                                              outline: "2px solid",
                                                              outlineColor: "primary.main",
                                                          },
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            <Box
                                                component={picture.imageUrl ? "img" : "div"}
                                                src={picture.imageUrl || undefined}
                                                alt={picture.title || offer.title}
                                                sx={{
                                                    width: 140,
                                                    height: 140,
                                                    objectFit: "cover",
                                                    borderRadius: 1,
                                                    display: "block",
                                                    bgcolor: "action.hover",
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 0.75,
                                                    fontWeight: 600,
                                                    lineHeight: 1.3,
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {picture.title}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
            )}
        </Box>
    );
}
